import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { useQuery } from '@apollo/client';
import { ArrowLeftRegular, EditRegular } from '@fluentui/react-icons';
import { Button, makeStyles } from '@fluentui/react-components';
import { useHistory, useParams } from 'react-router-dom';
import Card from '../../common/card/Card';
import ShimmeredDetailsListWrapper from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import {
  bodyContentContainer,
  iconButtonStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import FullWidthHeader from '../../common/headers/FullWidthHeader';
import CanAccess from '../../common/canAccess/CanAccess';
import SpanWithLabel from '../../common/SpanWithLabel';
import StackColumn from '../../common/StackColumn';

import getColumns from '../shared/EntityTerminationTaskList.config';
import {
  ILegalInProcessActionItemParams,
  IEntityTerminationTask,
  IEntityTerminationActionItem,
} from '../types';
import momentUtc from '../../../utils/DateFormatter';
import StatDateFormats from '../../../utils/types/DateFormats';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import EntityTerminationBlockerEditPanel from './EntityTerminationBlockerEditPanel';
import EntityTerminationBlockerTaskEditPanel from './EntityTerminationBlockerTaskEditPanel';
import {
  GET_BLOCKER_QUEUE_ACTION_ITEM,
  GET_BLOCKER_QUEUE_REVIEW,
} from '../../../utils/statApi/EntityTerminationApi';

const useStyles = makeStyles({
  sectionSpacing: { marginBottom: '20px' },
});

const EntityTerminationBlockerDetailPage = (): JSX.Element => {
  const history = useHistory();
  const { actionItemId: actionItemIdParam } = useParams<ILegalInProcessActionItemParams>();
  const actionItemId = actionItemIdParam ? Number.parseInt(actionItemIdParam, 10) : NaN;

  const [isEditDetailsPanelOpen, setIsEditDetailsPanelOpen] = useState<boolean>(false);
  const [isEditTaskPanelOpen, setIsEditTaskPanelOpen] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<IEntityTerminationTask | undefined>(undefined);

  const classes = useStyles();

  const { loading, error, data } = useQuery(GET_BLOCKER_QUEUE_ACTION_ITEM, {
    variables: { actionItemId },
    fetchPolicy: 'cache-and-network',
  });

  const blocker: IEntityTerminationActionItem | undefined = data?.actionItem;

  const refetchQuery = { query: GET_BLOCKER_QUEUE_ACTION_ITEM, variables: { actionItemId } };

  const blockerQueueReview = useQuery(GET_BLOCKER_QUEUE_REVIEW, {
    variables: { companyCode: blocker?.companyCode },
    skip: !blocker?.companyCode || !isEditDetailsPanelOpen,
    fetchPolicy: 'cache-and-network',
  });

  const existingBlockers: IEntityTerminationActionItem[] =
    blockerQueueReview?.data?.blockerQueueReview?.blockerQueueActionItems || [];

  const toggleEditDetailsPanel = () => {
    setIsEditDetailsPanelOpen(!isEditDetailsPanelOpen);
  };

  const toggleEditTaskPanel = (task?: IEntityTerminationTask | undefined) => {
    setSelectedTask(task);
    setIsEditTaskPanelOpen(!isEditTaskPanelOpen);
    if (isEditTaskPanelOpen) {
      setSelectedTask(undefined);
    }
  };

  const newTaskButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="BlockerQueue:EditActionItems">
        <>
          <Button appearance="primary" onClick={() => toggleEditTaskPanel()}>
            Add New Task
          </Button>
        </>
      </CanAccess>
    );
  };

  const renderHeader = (): JSX.Element => (
    <Stack horizontal>
      <h1>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular color={iconButtonStyles.color} />}
          style={{ marginRight: 10 }}
          title="Back to the Legal Entity Terminations"
          aria-label="Back to the Legal Entity Terminations"
          onClick={(): void => {
            history.push(`/monitoring-queue/${blocker?.companyCode}/blocker/`);
          }}
        />
        {blocker?.companyCode} - {blocker?.entityLegalName}
      </h1>
    </Stack>
  );

  const renderEditButton = (label: string): JSX.Element => {
    return (
      <Button
        appearance="subtle"
        title={`Edit ${label}`}
        aria-label={label}
        icon={<EditRegular color={iconButtonStyles.color} />}
        onClick={toggleEditDetailsPanel}
      />
    );
  };

  return (
    <>
      <FullWidthHeader title={renderHeader} />
      <div className={`${bodyContentContainer} lightBackground`}>
        {error && (
          <LoadingErrorMessage
            loading={loading}
            error={error}
            actionName="loading the blocker data"
          />
        )}

        <Stack>
          <Stack className={classes.sectionSpacing}>
            <Card
              title="Blocker Details"
              headingLevel={2}
              actionButton={renderEditButton('Blocker Details')}
            >
              <Stack horizontal wrap horizontalAlign="space-between" tokens={{ childrenGap: 20 }}>
                <StackColumn>
                  <SpanWithLabel
                    labelText="Blocker Category"
                    value={blocker?.legoCategory?.fieldName}
                  />
                  <SpanWithLabel labelText="Blocker Name" value={blocker?.description} />
                  <SpanWithLabel
                    labelText="Primary Blocker"
                    value={blocker?.isPrimaryBlocker ? 'Yes' : 'No'}
                  />
                  <SpanWithLabel labelText="Blocker Owner" value={blocker?.owner?.text} />
                  <SpanWithLabel labelText="Status" value={blocker?.status} />
                </StackColumn>
                <StackColumn>
                  <SpanWithLabel
                    labelText="Date Started"
                    value={
                      blocker?.dateStarted
                        ? momentUtc(blocker.dateStarted, StatDateFormats.TwoDigitDate, true)
                        : undefined
                    }
                  />
                  <SpanWithLabel
                    labelText="Date Completed"
                    value={
                      blocker?.dateCompleted
                        ? momentUtc(blocker.dateCompleted, StatDateFormats.TwoDigitDate, true)
                        : undefined
                    }
                  />
                </StackColumn>
              </Stack>
            </Card>
          </Stack>
          <Card title="Blocker Tasks" headingLevel={2} actionButton={newTaskButton()}>
            <ShimmeredDetailsListWrapper
              columns={getColumns(toggleEditTaskPanel)}
              items={blocker?.tasks || []}
              singularListLabel="Blocker Tasks"
              pluralListLabel="Blocker Tasks"
            />
          </Card>
        </Stack>
        {isEditDetailsPanelOpen && (
          <EntityTerminationBlockerEditPanel
            closePanel={toggleEditDetailsPanel}
            isNewBlocker={false}
            selectedBlocker={blocker}
            companyCode={blocker?.companyCode ?? ''}
            existingBlockers={existingBlockers}
            refetchQueries={[refetchQuery]}
          />
        )}
        {isEditTaskPanelOpen && (
          <EntityTerminationBlockerTaskEditPanel
            closePanel={toggleEditTaskPanel}
            isNewTask={!selectedTask}
            selectedTask={selectedTask}
            actionItemId={blocker?.id ?? 0}
            companyCode={blocker?.companyCode || ''}
            refetchQueries={[refetchQuery]}
            actionItemStartDate={blocker?.dateStarted}
          />
        )}
      </div>
    </>
  );
};

export default EntityTerminationBlockerDetailPage;
